import "./InternationalPhoneInput.css";
import { Form, Input,Select } from "antd";
import React, { useEffect,useMemo, useState } from "react";
import { Control, Controller, FieldError, FieldErrors, useWatch } from "react-hook-form";
import {
  CountryPhoneData,
  countryPhoneData,
  getCountryByDialCode,
  getPhoneValidationRules,
} from "../../utils/countryPhoneData";
import { InputField } from "./index";

const { Option } = Select;

type InternationalPhoneInputProps = {
  fieldname: string;
  countryCodeFieldname: string; // Separate field for country code
  label: string;
  control: Control<any>;
  setValue: (name: string, value: any) => void;
  errors?: FieldErrors<any>;
  isRequired?: boolean;
  defaultValue?: string;
  defaultCountryCode?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

/**
 * International Phone Input Component
 * Supports country flags, codes, and dynamic validation based on country
 */
export const InternationalPhoneInput: React.FC<InternationalPhoneInputProps> = ({
  fieldname,
  countryCodeFieldname,
  label,
  control,
  setValue,
  errors,
  isRequired = false,
  defaultValue,
  defaultCountryCode = "971", // UAE default
  placeholder,
  disabled = false,
  className = "",
}) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryPhoneData | undefined>(
    getCountryByDialCode(defaultCountryCode),
  );

  const countryCode = useWatch({ control, name: countryCodeFieldname });

  // Update selected country when country code changes
  useEffect(() => {
    // Use countryCode from form if available, otherwise use defaultCountryCode
    const effectiveCountryCode = countryCode || defaultCountryCode;
    const country = getCountryByDialCode(effectiveCountryCode);
    setSelectedCountry(country);
  }, [countryCode, defaultCountryCode]);

  // Get validation rules based on selected country
  const validationRules = useMemo(() => {
    return getPhoneValidationRules(selectedCountry, label, isRequired);
  }, [selectedCountry, label, isRequired]);

  // Generate placeholder based on country
  const dynamicPlaceholder = useMemo(() => {
    if (placeholder) return placeholder;
    if (selectedCountry) {
      return `0${"X".repeat(selectedCountry.mobileLength - 1)}`;
    }
    return "123456789";
  }, [selectedCountry, placeholder]);

  const fieldError = errors?.[fieldname] as FieldError | undefined;
  const countryCodeError = errors?.[countryCodeFieldname] as FieldError | undefined;

  return (
    <>
      <Form.Item
        required={isRequired}
        label={label}
        validateStatus={fieldError || countryCodeError ? "error" : undefined}
        help={fieldError?.message || countryCodeError?.message}
        className={className}
      >
        <Input.Group
          compact
          className={`international-phone-input-group ${fieldError || countryCodeError ? "error-state" : ""}`}
          style={{ display: "flex" }}
        >
          {/* Country Code Selector */}
          <Controller
            name={countryCodeFieldname}
            control={control}
            rules={{
              required: isRequired ? "Country code is required" : false,
            }}
            render={({ field }) => {
              // Use field.value if it exists, otherwise fallback to defaultCountryCode
              const selectValue = field.value || defaultCountryCode;
              return (
                <Select
                  {...field}
                  value={selectValue}
                  className="international-phone-select"
                  style={{ width: 105 }}
                  disabled={disabled}
                  showSearch
                  filterOption={(input, option) => {
                    // Handle label which can be ReactNode (string, number, JSX, etc.)
                    const labelText =
                      typeof option?.label === "string"
                        ? option.label
                        : typeof option?.label === "number"
                          ? String(option.label)
                          : "";
                    const value = String(option?.value ?? "");
                    const inputLower = input.toLowerCase();
                    return labelText.toLowerCase().includes(inputLower) || value.includes(input);
                  }}
                  onChange={(value) => {
                    field.onChange(value);
                    const country = getCountryByDialCode(value);
                    setSelectedCountry(country);
                    // Clear phone number when country changes to avoid invalid numbers
                    setValue(fieldname, "");
                  }}
                  optionLabelProp="label"
                >
                  {countryPhoneData.map((country) => (
                    <Option
                      key={country.code}
                      value={country.dialCode}
                      label={`${country.flag} +${country.dialCode} ${country.name}`}
                    >
                      <span style={{ marginRight: 8 }}>{country.flag}</span>
                      <span style={{ marginRight: 8 }}>+{country.dialCode}</span>
                      <span>{country.name}</span>
                    </Option>
                  ))}
                </Select>
              );
            }}
          />

          {/* Phone Number Input - Using InputField */}
          <InputField
            fieldname={fieldname}
            label=""
            control={control}
            setValue={setValue}
            initValue={defaultValue}
            isRequired={isRequired}
            disabled={disabled}
            rules={validationRules}
            iProps={{
              type: "tel",
              placeholder: dynamicPlaceholder,
              maxLength: selectedCountry?.mobileLength || 15,
              style: { flex: 1 },
            }}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, ""); // Only digits
              setValue(fieldname, value);
            }}
            formItemStyle={{ marginBottom: 0, flex: 1 }}
          />
        </Input.Group>
      </Form.Item>
    </>
  );
};
